<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell>

  <app-page-hero
    eyebrow="Administration"
    title="WhatsApp Numbers"
    subtitle="Register tenant-owned WASender numbers, credentials, and sending limits.">
    <div actions>
      <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary" (click)="loadAccounts()"></p-button>
      <p-button label="Add Number" icon="pi pi-plus" (click)="openNew()" [disabled]="!canManage"></p-button>
    </div>
  </app-page-hero>

  <app-page-data-panel
    title="WASender Accounts"
    subtitle="Credentials are stored encrypted and only masked values are returned by the API."
    [resultText]="accounts.length + ' configured'">
    <p-table [value]="accounts" styleClass="p-datatable-sm" responsiveLayout="scroll" [loading]="isLoading">
      <ng-template pTemplate="header">
        <tr>
          <th>Number</th>
          <th>Session</th>
          <th>Credentials</th>
          <th>Rate Profile</th>
          <th>Status</th>
          <th class="text-right">Actions</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-account>
        <tr>
          <td>
            <div class="flex flex-column gap-1">
              <span class="font-semibold">{{ account.displayName }}</span>
              <small>{{ account.phoneNumber }}</small>
            </div>
          </td>
          <td>
            <div class="flex flex-column gap-1">
              <span>{{ account.sessionId }}</span>
              <small>Provider session</small>
            </div>
          </td>
          <td>
            <div class="credential-stack">
              <span><strong>API:</strong> {{ account.maskedApiKey || '-' }}</span>
              <span><strong>Webhook:</strong> {{ account.maskedWebhookSecret || '-' }}</span>
            </div>
          </td>
          <td>
            <div class="flex flex-column gap-1">
              <span>{{ account.rateProfile || 'ACCOUNT_PROTECTION' }}</span>
              <small>{{ rateProfileDescription(account.rateProfile || 'ACCOUNT_PROTECTION') }}</small>
            </div>
          </td>
          <td><p-tag [value]="statusLabel(account)" [severity]="statusSeverity(account)"></p-tag></td>
          <td class="text-right">
            <div class="flex justify-content-end gap-1">
              <p-button icon="pi pi-pencil" [text]="true" pTooltip="Edit number" (click)="editAccount(account)" [disabled]="!canManage"></p-button>
              <p-button icon="pi pi-check-circle" [text]="true" severity="success" pTooltip="Set default"
                (click)="setDefault(account)" [disabled]="!canManage || account.defaultAccount"></p-button>
              <p-button icon="pi pi-send" [text]="true" severity="secondary" pTooltip="Queue test message"
                [loading]="testingAccountId === account.id" (click)="testAccount(account)" [disabled]="!canManage || account.active === false"></p-button>
            </div>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6" class="text-center p-4">No WASender numbers have been registered.</td>
        </tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>
</app-page-shell>

<p-dialog
  [header]="accountForm.get('id')?.value ? 'Edit WASender Number' : 'Add WASender Number'"
  [(visible)]="accountDialogVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: 'min(860px, 96vw)' }">
  <form [formGroup]="accountForm" class="p-fluid wasender-setup-form">
    <div class="formgrid grid">
      <div class="field col-12 md:col-6">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-id-card"></i></p-inputGroupAddon>
          <p-iftaLabel class="flex-1">
            <input pInputText formControlName="displayName" />
            <label>Display Name *</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-6">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-whatsapp"></i></p-inputGroupAddon>
          <p-iftaLabel class="flex-1">
            <input pInputText formControlName="phoneNumber" placeholder="+9665..." />
            <label>WhatsApp Number *</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-6">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-key"></i></p-inputGroupAddon>
          <p-iftaLabel class="flex-1">
            <input pInputText formControlName="sessionId" />
            <label>WASender Session ID *</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-6">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-clock"></i></p-inputGroupAddon>
          <p-iftaLabel class="flex-1">
            <p-select [options]="rateProfiles" formControlName="rateProfile" optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"></p-select>
            <label>Rate Profile *</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-6">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-lock"></i></p-inputGroupAddon>
          <p-iftaLabel class="flex-1">
            <input pInputText type="password" formControlName="apiKey" autocomplete="new-password" />
            <label>API Key {{ accountForm.get('id')?.value ? '(leave blank to keep)' : '*' }}</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <div class="field col-12 md:col-6">
        <p-inputGroup>
          <p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon>
          <p-iftaLabel class="flex-1">
            <input pInputText type="password" formControlName="webhookSecret" autocomplete="new-password" />
            <label>Webhook Secret {{ accountForm.get('id')?.value ? '(leave blank to keep)' : '*' }}</label>
          </p-iftaLabel>
        </p-inputGroup>
      </div>
      <ng-container *ngIf="showCustomLimits()">
        <div class="field col-12 md:col-6">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-stopwatch"></i></p-inputGroupAddon>
            <p-iftaLabel class="flex-1">
              <p-inputNumber formControlName="customMinSpacingSeconds" [min]="1" [useGrouping]="false" styleClass="w-full"></p-inputNumber>
              <label>Minimum Spacing Seconds</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>
        <div class="field col-12 md:col-6">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-calendar-clock"></i></p-inputGroupAddon>
            <p-iftaLabel class="flex-1">
              <p-inputNumber formControlName="customDailyCap" [min]="1" [useGrouping]="false" styleClass="w-full"></p-inputNumber>
              <label>Daily Cap</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>
      </ng-container>
      <div class="field col-12 md:col-6 flex align-items-center gap-2">
        <p-toggleSwitch formControlName="active"></p-toggleSwitch>
        <span>Active</span>
      </div>
      <div class="field col-12 md:col-6 flex align-items-center gap-2">
        <p-toggleSwitch formControlName="defaultAccount"></p-toggleSwitch>
        <span>Default sender</span>
      </div>
    </div>
  </form>
  <ng-template pTemplate="footer">
    <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="accountDialogVisible = false"></p-button>
    <p-button label="Save Number" icon="pi pi-check" (click)="saveAccount()" [disabled]="accountForm.invalid || !canManage"></p-button>
  </ng-template>
</p-dialog>
